import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { getDate, getDatePlusMinus, getUnixTime } from '@einhaus/meteoric-helpers';
import { OBVDivergenceType, OBVEventType, type BarDuration, type ChartMarker, type OBVAnalyzerEvent } from '@einhaus/project314-types';

import ChartContainer from '@/components/Chart/ChartContainer';
import Badge from '@/components/UI/Badge';
import { DataLoadingSpinner } from '@/components/UI/DataLoadingSpinner';
import { trpc } from '@/api/trpc';
import { useAppQuery } from '@/hooks/useAppQuery';
import { getEntityLink } from '@/utils/entityHelpers';
import { hideInitialSpinner } from '@/utils/spinnerControl';
import OBVLineAuditChart from './OBVLineAuditChart';

type AuditRenderVariant = 'focused' | 'wide';

const AUDIT_RENDER_VARIANTS: AuditRenderVariant[] = ['wide', 'focused'];
const CHART_LOADING_READY_SELECTOR = '.chart-container[data-chart-loading-state="ready"]';
const CHART_LOADING_EMPTY_SELECTOR = '.chart-container[data-chart-loading-state="empty"]';
const CHART_LOADING_ERROR_SELECTOR = '.chart-container[data-chart-loading-state="error"]';
const CHART_LOADING_OVERLAY_SELECTOR = '[data-chart-loading-overlay="true"]';
const MIN_CAPTURE_CANVAS_WIDTH = 500;
const MIN_CAPTURE_CANVAS_HEIGHT = 200;
const MIN_CAPTURE_READY_CANVAS_COUNT = 3;
const DEFAULT_WINDOW_DAYS = 180;
const DEFAULT_END_DATE_TIME = '2026-04-06 22:49:30';
const DEFAULT_DURATION: BarDuration = '30m';
const DEFAULT_AFTER_HOURS_REMOVAL = true;
const FOCUSED_MARKER_CONTEXT_BARS = 10;

const ALL_OBV_EVENT_TYPES: readonly OBVAnalyzerEvent['type'][] = [
    OBVEventType.crossedAboveSignal,
    OBVEventType.crossedBelowSignal,
    OBVEventType.bullishDivergenceDetected,
    OBVEventType.bearishDivergenceDetected,
    OBVEventType.hiddenBullishDivergence,
    OBVEventType.hiddenBearishDivergence,
    OBVEventType.divergenceConfirmed,
    OBVEventType.divergenceExpired,
    OBVEventType.bullishBreakoutConfirmed,
    OBVEventType.bearishBreakdownConfirmed,
    OBVEventType.enteredAccumulation,
    OBVEventType.enteredDistribution,
    OBVEventType.newLocalHigh,
    OBVEventType.newLocalLow
];

const OBV_EVENT_TYPE_SET = new Set<string>(ALL_OBV_EVENT_TYPES);

const isAuditRenderVariant = (value: string | null): value is AuditRenderVariant =>
    value !== null && AUDIT_RENDER_VARIANTS.includes(value as AuditRenderVariant);

const isAuditDuration = (value: string | null): value is BarDuration => {
    if (!value) return false;
    return ['1m', '5m', '15m', '30m', '60m', '120m', '180m', '240m', '1d'].includes(value);
};

const parseBooleanSearchParam = (value: string | null, defaultValue: boolean): boolean => {
    if (value === null) return defaultValue;
    if (value === '1' || value.toLowerCase() === 'true') return true;
    if (value === '0' || value.toLowerCase() === 'false') return false;
    return defaultValue;
};

const parsePositiveInteger = (value: string | null, fallbackValue: number): number => {
    const parsed = Number(value);

    if (!Number.isInteger(parsed) || parsed <= 0) {
        return fallbackValue;
    }

    return parsed;
};

const isObvEventType = (value: string): value is OBVAnalyzerEvent['type'] => OBV_EVENT_TYPE_SET.has(value);

const parseSelectedEventTypes = (value: string | null): Set<OBVAnalyzerEvent['type']> => {
    if (!value) {
        return new Set(ALL_OBV_EVENT_TYPES);
    }

    const selectedTypes = value
        .split(',')
        .map((item) => item.trim())
        .filter(isObvEventType);

    if (selectedTypes.length === 0) {
        return new Set(ALL_OBV_EVENT_TYPES);
    }

    return new Set(selectedTypes);
};

const getWindowPreset = (duration: BarDuration, variant: AuditRenderVariant): { afterDays: number; beforeDays: number } => {
    if (variant === 'focused') {
        if (duration === '1m') return { beforeDays: 2, afterDays: 1 };
        if (duration === '5m') return { beforeDays: 8, afterDays: 2 };
        if (duration === '15m') return { beforeDays: 14, afterDays: 4 };
        if (duration === '30m') return { beforeDays: 25, afterDays: 8 };
        if (duration === '60m') return { beforeDays: 50, afterDays: 15 };
        if (duration === '120m') return { beforeDays: 70, afterDays: 20 };
        if (duration === '180m') return { beforeDays: 90, afterDays: 25 };
        if (duration === '240m') return { beforeDays: 120, afterDays: 30 };
        return { beforeDays: 180, afterDays: 45 };
    }

    if (duration === '1m') return { beforeDays: 5, afterDays: 2 };
    if (duration === '5m') return { beforeDays: 16, afterDays: 4 };
    if (duration === '15m') return { beforeDays: 30, afterDays: 8 };
    if (duration === '30m') return { beforeDays: 55, afterDays: 16 };
    if (duration === '60m') return { beforeDays: 105, afterDays: 30 };
    if (duration === '120m') return { beforeDays: 150, afterDays: 40 };
    if (duration === '180m') return { beforeDays: 180, afterDays: 55 };
    if (duration === '240m') return { beforeDays: 240, afterDays: 70 };
    return { beforeDays: 360, afterDays: 90 };
};

const getRenderChartWindow = (params: {
    duration: BarDuration;
    fallbackEndDateTime: string;
    focusDateTime: string | null;
    variant: AuditRenderVariant;
    windowDays: number;
}): { endDateTime: string; startDateTime: string } => {
    const { duration, fallbackEndDateTime, focusDateTime, variant, windowDays } = params;

    if (!focusDateTime) {
        return {
            endDateTime: fallbackEndDateTime,
            startDateTime:
                variant === 'wide'
                    ? getDatePlusMinus('-', windowDays, 'day', 'ymdhms', 'start', fallbackEndDateTime)
                    : getDatePlusMinus('-', Math.min(windowDays, 90), 'day', 'ymdhms', 'start', fallbackEndDateTime)
        };
    }

    const windowPreset = getWindowPreset(duration, variant);

    return {
        endDateTime: getDatePlusMinus('+', windowPreset.afterDays, 'day', 'ymdhms', 'end', focusDateTime),
        startDateTime: getDatePlusMinus('-', windowPreset.beforeDays, 'day', 'ymdhms', 'start', focusDateTime)
    };
};

const formatEnumLabel = (value: string | null | undefined): string => {
    if (!value) return 'n/a';

    return value
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .replace(/_/g, ' ')
        .replace(/^./, (firstLetter) => firstLetter.toUpperCase());
};

const getEventTone = (event: OBVAnalyzerEvent): 'bullish' | 'bearish' | 'neutral' => {
    if (event.divergenceType === OBVDivergenceType.bullish || event.divergenceType === OBVDivergenceType.hiddenBullish) {
        return 'bullish';
    }

    if (event.divergenceType === OBVDivergenceType.bearish || event.divergenceType === OBVDivergenceType.hiddenBearish) {
        return 'bearish';
    }

    const bullishEventTypes: readonly OBVAnalyzerEvent['type'][] = [
        OBVEventType.crossedAboveSignal,
        OBVEventType.bullishBreakoutConfirmed,
        OBVEventType.enteredAccumulation,
        OBVEventType.newLocalHigh
    ];

    const bearishEventTypes: readonly OBVAnalyzerEvent['type'][] = [
        OBVEventType.crossedBelowSignal,
        OBVEventType.bearishBreakdownConfirmed,
        OBVEventType.enteredDistribution,
        OBVEventType.newLocalLow
    ];

    if (bullishEventTypes.includes(event.type)) return 'bullish';
    if (bearishEventTypes.includes(event.type)) return 'bearish';
    return 'neutral';
};

const getToneMarkerLabel = (tone: 'bullish' | 'bearish' | 'neutral'): string => {
    if (tone === 'bullish') return 'L';
    if (tone === 'bearish') return 'S';
    return 'N';
};

const getEventMarkerPosition = (event: OBVAnalyzerEvent): ChartMarker['position'] => {
    const tone = getEventTone(event);
    if (tone === 'bullish') return 'belowBar';
    if (tone === 'bearish') return 'aboveBar';
    return 'inBar';
};

const buildCompactMarkerText = (event: OBVAnalyzerEvent): string => {
    if (event.type === OBVEventType.crossedAboveSignal) return 'OBV Cross Long';
    if (event.type === OBVEventType.crossedBelowSignal) return 'OBV Cross Short';
    if (event.type === OBVEventType.bullishBreakoutConfirmed) return 'OBV Breakout Long';
    if (event.type === OBVEventType.bearishBreakdownConfirmed) return 'OBV Breakdown Short';
    if (event.type === OBVEventType.enteredAccumulation) return 'OBV Accum Long';
    if (event.type === OBVEventType.enteredDistribution) return 'OBV Dist Short';
    if (event.type === OBVEventType.newLocalHigh) return 'OBV New High Long';
    if (event.type === OBVEventType.newLocalLow) return 'OBV New Low Short';
    if (event.divergenceType) return `OBV ${formatEnumLabel(event.divergenceType)}`;
    return formatEnumLabel(event.type);
};

const getBadgeVariant = (tone: 'bullish' | 'bearish' | 'neutral'): 'success' | 'danger' | 'secondary' => {
    if (tone === 'bullish') return 'success';
    if (tone === 'bearish') return 'danger';
    return 'secondary';
};

const OBVAuditRender = () => {
    const { entityId = '' } = useParams<{ entityId: string }>();
    const [searchParams] = useSearchParams();
    const parsedEntityId = Number(entityId);
    const requestedVariant = searchParams.get('variant');
    const resolvedVariant: AuditRenderVariant = requestedVariant && isAuditRenderVariant(requestedVariant) ? requestedVariant : 'focused';
    const requestedDuration = searchParams.get('duration');
    const resolvedDuration: BarDuration = isAuditDuration(requestedDuration) ? requestedDuration : DEFAULT_DURATION;
    const resolvedEndDateTime = searchParams.get('endDateTime') ?? DEFAULT_END_DATE_TIME;
    const resolvedFocusDateTime = searchParams.get('focusDateTime');
    const resolvedWindowDays = parsePositiveInteger(searchParams.get('windowDays'), DEFAULT_WINDOW_DAYS);
    const resolvedShouldRemoveAfterHours = parseBooleanSearchParam(searchParams.get('shouldRemoveAfterHours'), DEFAULT_AFTER_HOURS_REMOVAL);
    const selectedEventTypes = parseSelectedEventTypes(searchParams.get('eventTypes'));
    const captureMode = parseBooleanSearchParam(searchParams.get('capture'), false);
    const captureFrameRef = useRef<HTMLDivElement | null>(null);
    const [hasChartRenderSettled, setHasChartRenderSettled] = useState(false);
    const [isCaptureReady, setIsCaptureReady] = useState(false);
    const [renderFailureReason, setRenderFailureReason] = useState<string | null>(null);

    const auditQueryStartDateTime = useMemo(
        () => getDatePlusMinus('-', resolvedWindowDays, 'day', 'ymdhms', 'start', resolvedEndDateTime),
        [resolvedEndDateTime, resolvedWindowDays]
    );

    const entityQuery = useAppQuery({
        ...trpc.entity.details.queryOptions({
            entityId: Number.isInteger(parsedEntityId) && parsedEntityId > 0 ? parsedEntityId : 0,
            entityType: 'Asset'
        }),
        enabled: Number.isInteger(parsedEntityId) && parsedEntityId > 0
    });

    const entity = entityQuery.data?.entity ?? null;

    const auditQuery = useAppQuery({
        ...trpc.entity.fetchObvAudit.queryOptions({
            entityId: parsedEntityId,
            entityType: 'Asset',
            startDateTime: auditQueryStartDateTime,
            endDateTime: resolvedEndDateTime,
            duration: resolvedDuration,
            focusDateTime: resolvedFocusDateTime ?? undefined,
            shouldRemoveAfterHours: resolvedShouldRemoveAfterHours
        }),
        enabled: Number.isInteger(parsedEntityId) && parsedEntityId > 0
    });

    const filteredEvents = useMemo(
        () => (auditQuery.data?.events ?? []).filter((eventRow) => selectedEventTypes.has(eventRow.event.type)),
        [auditQuery.data?.events, selectedEventTypes]
    );

    const selectedDateTime = resolvedFocusDateTime ?? filteredEvents[0]?.dateTime ?? auditQuery.data?.latestState?.dateTime ?? null;

    const selectedEvents = useMemo(
        () => (selectedDateTime ? filteredEvents.filter((eventRow) => eventRow.dateTime === selectedDateTime) : []),
        [filteredEvents, selectedDateTime]
    );

    const selectedEventMarkerLabelByKey = useMemo(() => {
        const markerLabelMap = new Map<string, string>();
        const toneOrdinalByTone = new Map<'bullish' | 'bearish' | 'neutral', number>();

        for (const eventRow of selectedEvents) {
            const eventTone = getEventTone(eventRow.event);
            const nextOrdinal = (toneOrdinalByTone.get(eventTone) ?? 0) + 1;
            toneOrdinalByTone.set(eventTone, nextOrdinal);

            markerLabelMap.set(
                `${eventRow.dateTime}:${eventRow.event.type}:${eventRow.event.barIndex}`,
                `${getToneMarkerLabel(eventTone)}${nextOrdinal}`
            );
        }

        return markerLabelMap;
    }, [selectedEvents]);

    const chartWindow = useMemo(
        () =>
            getRenderChartWindow({
                duration: resolvedDuration,
                fallbackEndDateTime: resolvedEndDateTime,
                focusDateTime: selectedDateTime,
                variant: resolvedVariant,
                windowDays: resolvedWindowDays
            }),
        [resolvedDuration, resolvedEndDateTime, selectedDateTime, resolvedVariant, resolvedWindowDays]
    );

    const chartWindowTimestampRange = useMemo(
        () => ({
            endTimestamp: getUnixTime(chartWindow.endDateTime),
            startTimestamp: getUnixTime(chartWindow.startDateTime)
        }),
        [chartWindow.endDateTime, chartWindow.startDateTime]
    );

    const visibleFilteredEvents = useMemo(
        () =>
            filteredEvents.filter(
                (eventRow) =>
                    eventRow.event.timestamp >= chartWindowTimestampRange.startTimestamp &&
                    eventRow.event.timestamp <= chartWindowTimestampRange.endTimestamp
            ),
        [chartWindowTimestampRange.endTimestamp, chartWindowTimestampRange.startTimestamp, filteredEvents]
    );

    const visibleStateHistory = useMemo(
        () =>
            (auditQuery.data?.stateHistory ?? []).filter(
                (stateRow) =>
                    stateRow.timestamp >= chartWindowTimestampRange.startTimestamp &&
                    stateRow.timestamp <= chartWindowTimestampRange.endTimestamp
            ),
        [auditQuery.data?.stateHistory, chartWindowTimestampRange.endTimestamp, chartWindowTimestampRange.startTimestamp]
    );

    const visibleStateIndexByDateTime = useMemo(() => {
        const indexMap = new Map<string, number>();

        for (const [index, stateRow] of visibleStateHistory.entries()) {
            indexMap.set(stateRow.dateTime, index);
        }

        return indexMap;
    }, [visibleStateHistory]);

    const chartMarkerEvents = useMemo(() => {
        if (resolvedVariant === 'wide' || !selectedDateTime) {
            return visibleFilteredEvents;
        }

        const selectedIndex = visibleStateIndexByDateTime.get(selectedDateTime);

        if (selectedIndex === undefined) {
            return visibleFilteredEvents.filter((eventRow) => eventRow.dateTime === selectedDateTime);
        }

        const groupedEventsByDateTime = new Map<string, { distanceFromFocus: number; rows: typeof visibleFilteredEvents }>();

        for (const eventRow of visibleFilteredEvents) {
            const eventIndex = visibleStateIndexByDateTime.get(eventRow.dateTime);

            if (eventIndex === undefined) {
                continue;
            }

            const existingGroup = groupedEventsByDateTime.get(eventRow.dateTime);

            if (existingGroup) {
                existingGroup.rows.push(eventRow);
                continue;
            }

            groupedEventsByDateTime.set(eventRow.dateTime, {
                distanceFromFocus: Math.abs(eventIndex - selectedIndex),
                rows: [eventRow]
            });
        }

        return [...groupedEventsByDateTime.values()]
            .sort((a, b) => {
                if (a.distanceFromFocus !== b.distanceFromFocus) {
                    return a.distanceFromFocus - b.distanceFromFocus;
                }

                const firstTimestamp = a.rows[0]?.event.timestamp ?? 0;
                const secondTimestamp = b.rows[0]?.event.timestamp ?? 0;
                return firstTimestamp - secondTimestamp;
            })
            .slice(0, FOCUSED_MARKER_CONTEXT_BARS)
            .flatMap((group) => group.rows)
            .sort((a, b) => a.event.timestamp - b.event.timestamp);
    }, [resolvedVariant, selectedDateTime, visibleFilteredEvents, visibleStateIndexByDateTime]);

    const markers = useMemo<ChartMarker[]>(
        () =>
            chartMarkerEvents.map((eventRow) => ({
                dateTime: eventRow.dateTime,
                position: getEventMarkerPosition(eventRow.event),
                text:
                    eventRow.dateTime === selectedDateTime
                        ? (selectedEventMarkerLabelByKey.get(`${eventRow.dateTime}:${eventRow.event.type}:${eventRow.event.barIndex}`) ??
                          buildCompactMarkerText(eventRow.event))
                        : getToneMarkerLabel(getEventTone(eventRow.event))
            })),
        [chartMarkerEvents, selectedDateTime, selectedEventMarkerLabelByKey]
    );

    const obvChartPoints = useMemo(
        () =>
            visibleStateHistory.map((stateRow) => ({
                dateTime: stateRow.dateTime,
                signal: stateRow.state.line.signal,
                timestamp: stateRow.timestamp,
                value: stateRow.state.line.value
            })),
        [visibleStateHistory]
    );

    const obvChartMarkers = useMemo(
        () =>
            chartMarkerEvents.map((eventRow) => ({
                dateTime: eventRow.dateTime,
                position: getEventMarkerPosition(eventRow.event),
                text:
                    eventRow.dateTime === selectedDateTime
                        ? (selectedEventMarkerLabelByKey.get(`${eventRow.dateTime}:${eventRow.event.type}:${eventRow.event.barIndex}`) ??
                          buildCompactMarkerText(eventRow.event))
                        : getToneMarkerLabel(getEventTone(eventRow.event)),
                tone: getEventTone(eventRow.event)
            })),
        [chartMarkerEvents, selectedDateTime, selectedEventMarkerLabelByKey]
    );

    useEffect(() => {
        hideInitialSpinner();
    }, []);

    useEffect(() => {
        setHasChartRenderSettled(false);
        setIsCaptureReady(false);
        setRenderFailureReason(null);
    }, [parsedEntityId, resolvedDuration, resolvedEndDateTime, resolvedFocusDateTime, resolvedVariant, resolvedWindowDays]);

    useEffect(() => {
        if (!entity || !auditQuery.data) return;
        setHasChartRenderSettled(true);
    }, [auditQuery.data, entity]);

    useEffect(() => {
        if (!hasChartRenderSettled) return;

        let animationFrameId: number | null = null;

        const evaluateCaptureReadiness = () => {
            const captureFrame = captureFrameRef.current;

            if (!captureFrame) {
                animationFrameId = window.requestAnimationFrame(evaluateCaptureReadiness);
                return;
            }

            const chartContainer = captureFrame.querySelector(CHART_LOADING_READY_SELECTOR);
            const emptyChartContainer = captureFrame.querySelector(CHART_LOADING_EMPTY_SELECTOR);
            const errorChartContainer = captureFrame.querySelector(CHART_LOADING_ERROR_SELECTOR);
            const loadingOverlay = captureFrame.querySelector(CHART_LOADING_OVERLAY_SELECTOR);

            if (loadingOverlay) {
                animationFrameId = window.requestAnimationFrame(evaluateCaptureReadiness);
                return;
            }

            if (errorChartContainer) {
                setRenderFailureReason('chart-error');
                return;
            }

            if (emptyChartContainer) {
                setRenderFailureReason('no-bars');
                return;
            }

            if (!chartContainer) {
                animationFrameId = window.requestAnimationFrame(evaluateCaptureReadiness);
                return;
            }

            const readyCanvases = Array.from(captureFrame.querySelectorAll('canvas')).filter((canvas) => {
                return canvas.width >= MIN_CAPTURE_CANVAS_WIDTH && canvas.height >= MIN_CAPTURE_CANVAS_HEIGHT;
            });

            if (readyCanvases.length < MIN_CAPTURE_READY_CANVAS_COUNT) {
                animationFrameId = window.requestAnimationFrame(evaluateCaptureReadiness);
                return;
            }

            window.requestAnimationFrame(() => {
                window.requestAnimationFrame(() => {
                    setIsCaptureReady(true);
                });
            });
        };

        animationFrameId = window.requestAnimationFrame(evaluateCaptureReadiness);

        return () => {
            if (animationFrameId !== null) {
                window.cancelAnimationFrame(animationFrameId);
            }
        };
    }, [hasChartRenderSettled]);

    if (!Number.isInteger(parsedEntityId) || parsedEntityId <= 0) {
        return <div className="p-6 text-red-300">Invalid entity id.</div>;
    }

    if (entityQuery.isLoading || auditQuery.isLoading) {
        return (
            <div className="min-h-screen bg-zinc-950 text-white flex items-center justify-center">
                <DataLoadingSpinner text="Loading OBV audit render..." />
            </div>
        );
    }

    if (entityQuery.error) {
        return <div className="p-6 text-red-300">{entityQuery.error.message}</div>;
    }

    if (auditQuery.error) {
        return <div className="p-6 text-red-300">{auditQuery.error.message}</div>;
    }

    if (!entity || !auditQuery.data) {
        return <div className="p-6 text-zinc-300">OBV audit render data not available.</div>;
    }

    return (
        <div className="min-h-screen bg-[#0a0a0a] text-white px-6 py-5">
            <div className="mx-auto max-w-[1840px] space-y-4">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div>
                        <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">OBV Audit Render</div>
                        <h1 className="text-3xl font-semibold">
                            {entity.symbol} <span className="text-zinc-500 text-xl">{entity.name ?? ''}</span>
                        </h1>
                        <div className="text-sm text-zinc-400">
                            entityId={entity.id} · duration={resolvedDuration} · variant={resolvedVariant} · focus=
                            {selectedDateTime ?? 'n/a'}
                        </div>
                    </div>

                    <div className="flex flex-wrap items-center gap-3 text-sm">
                        <Link
                            className="text-blue-400 hover:text-blue-300 underline"
                            to={`${getEntityLink(entity)}?duration=${resolvedDuration}`}
                        >
                            Open Full Entity Page
                        </Link>
                    </div>
                </div>

                <div className="flex flex-wrap items-center gap-2 text-xs">
                    {AUDIT_RENDER_VARIANTS.map((candidateVariant) => (
                        <Link
                            className={`rounded border px-3 py-1 ${
                                candidateVariant === resolvedVariant
                                    ? 'border-blue-500 bg-blue-500/10 text-blue-300'
                                    : 'border-zinc-700 text-zinc-400 hover:text-zinc-200'
                            }`}
                            key={candidateVariant}
                            to={`/tools/obvAudit/render/${entity.id}?duration=${resolvedDuration}&endDateTime=${encodeURIComponent(
                                resolvedEndDateTime
                            )}&windowDays=${String(resolvedWindowDays)}&shouldRemoveAfterHours=${
                                resolvedShouldRemoveAfterHours ? '1' : '0'
                            }&variant=${candidateVariant}&eventTypes=${encodeURIComponent([...selectedEventTypes].join(','))}${
                                selectedDateTime ? `&focusDateTime=${encodeURIComponent(selectedDateTime)}` : ''
                            }${captureMode ? '&capture=1' : ''}`}
                        >
                            {candidateVariant}
                        </Link>
                    ))}
                </div>

                <div
                    className="mx-auto w-[1728px] max-w-[1728px] space-y-4"
                    data-render-failure-reason={renderFailureReason ?? undefined}
                    data-render-ready={captureMode ? (isCaptureReady ? 'true' : 'false') : undefined}
                    id="obvAuditCaptureFrame"
                    ref={captureFrameRef}
                >
                    <div className="rounded-xl border border-zinc-800 bg-[#0f0f0f] px-5 py-4">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <div className="space-y-1">
                                <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500">Focused review context</div>
                                <div className="text-sm text-zinc-300">
                                    {selectedDateTime ?? 'n/a'} · {resolvedVariant === 'focused' ? 'focused neighborhood' : 'wide context'}
                                </div>
                            </div>
                            <div className="text-xs text-zinc-500">
                                chart window {chartWindow.startDateTime} → {chartWindow.endDateTime}
                            </div>
                        </div>

                        {selectedEvents.length > 0 ? (
                            <div className="mt-4 flex flex-wrap gap-2">
                                {selectedEvents.map((eventRow) => {
                                    const eventKey = `${eventRow.dateTime}:${eventRow.event.type}:${eventRow.event.barIndex}`;
                                    const markerId = selectedEventMarkerLabelByKey.get(eventKey) ?? buildCompactMarkerText(eventRow.event);

                                    return (
                                        <Badge className="px-3 py-1" key={eventKey} variant={getBadgeVariant(getEventTone(eventRow.event))}>
                                            {markerId} = {formatEnumLabel(eventRow.event.type)}
                                        </Badge>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="mt-4 text-sm text-zinc-500">No focused event guide for the selected bar.</div>
                        )}
                    </div>

                    <div className="rounded-xl border border-zinc-800 bg-[#0f0f0f] p-4">
                        <ChartContainer
                            hideComparisonSearch
                            hideControls
                            shouldIncludeVolumeSeries
                            duration={resolvedDuration}
                            endDateTime={chartWindow.endDateTime}
                            entity={entity}
                            markersOverride={markers}
                            startDateTime={chartWindow.startDateTime}
                        />
                    </div>

                    <div className="rounded-xl border border-zinc-800 bg-[#0f0f0f] p-4">
                        <OBVLineAuditChart
                            eventMarkers={obvChartMarkers}
                            focusedDateTime={selectedDateTime}
                            height={320}
                            points={obvChartPoints}
                        />
                    </div>
                </div>

                {!captureMode ? (
                    <div className="text-xs text-zinc-500">
                        Generated {getDate({ format: 'ymdhms' })}. This page is designed for deterministic OBV screenshots and AI review.
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default OBVAuditRender;
